/**
 * Budget calculation utilities to ensure consistency across the application
 */

/**
 * Calculate base hours from items (excluding supervision and supervision-only items)
 * @param {Array} items - Array of budget items
 * @returns {Number} Base hours rounded to 1 decimal place
 */
export function calculateBaseHours(items) {
  if (!Array.isArray(items)) return 0;
  
  const base = items.reduce((sum, item) => {
    // Handle different item structures
    if (item.service) {
      // New structure: item has service object
      if (item.service.name === "Supervisión Creativa") return sum;
      // Skip supervision-only items in base calculation
      if (item.supervisionOnly) return sum;
      // Use totalHours if available, otherwise calculate from service hours
      const totalHours = parseFloat(item.totalHours) || 
                        (parseFloat(item.service.hours) || parseFloat(item.service.defaultHours) || 0) * (parseFloat(item.quantity) || 1);
      return sum + totalHours;
    } else if (item.name && item.hours !== undefined) {
      // Legacy/database structure: item has direct properties
      if (item.name === "Supervisión Creativa") return sum;
      // Skip supervision-only items in base calculation
      if (item.supervisionOnly) return sum;
      const totalHours = parseFloat(item.totalHours) || 
                        (parseFloat(item.hours) || 0) * (parseFloat(item.quantity) || 1);
      return sum + totalHours;
    }
    return sum;
  }, 0);
  
  return Math.round(base * 10) / 10;
}

/**
 * Calculate supervision-only hours from items
 * @param {Array} items - Array of budget items
 * @returns {Number} Supervision-only hours rounded to 1 decimal place
 */
export function calculateSupervisionOnlyHours(items) {
  if (!Array.isArray(items)) return 0;
  
  const supervisionOnlyTotal = items.reduce((sum, item) => {
    // Only include items marked as supervision-only
    if (item.supervisionOnly) {
      if (item.service) {
        // New structure: item has service object
        const totalHours = parseFloat(item.totalHours) || 
                          (parseFloat(item.service.hours) || parseFloat(item.service.defaultHours) || 0) * (parseFloat(item.quantity) || 1);
        return sum + totalHours;
      } else if (item.name && item.hours !== undefined) {
        // Legacy/database structure: item has direct properties
        const totalHours = parseFloat(item.totalHours) || 
                          (parseFloat(item.hours) || 0) * (parseFloat(item.quantity) || 1);
        return sum + totalHours;
      }
    }
    return sum;
  }, 0);
  
  return Math.round(supervisionOnlyTotal * 10) / 10;
}

/**
 * Calculate supervision hours from items
 * @param {Array} items - Array of budget items
 * @returns {Number} Supervision hours rounded to 1 decimal place
 */
export function calculateSupervisionHours(items) {
  if (!Array.isArray(items)) return 0;
  
  const supervisionItem = items.find(item => {
    if (item.service) {
      // New structure: item has service object
      return item.service.name === "Supervisión Creativa";
    } else if (item.name) {
      // Legacy/database structure: item has direct properties
      return item.name === "Supervisión Creativa";
    }
    return false;
  });
  
  if (!supervisionItem) return 0;
  
  // Use totalHours if available, otherwise calculate
  if (supervisionItem.totalHours !== undefined) {
    return Math.round(parseFloat(supervisionItem.totalHours) * 10) / 10;
  }
  
  let hours, quantity;
  if (supervisionItem.service) {
    // New structure
    hours = parseFloat(supervisionItem.service.hours) || 0;
    quantity = parseFloat(supervisionItem.quantity) || 1;
  } else {
    // Legacy/database structure
    hours = parseFloat(supervisionItem.hours) || 0;
    quantity = parseFloat(supervisionItem.quantity) || 1;
  }
  
  return Math.round((hours * quantity) * 10) / 10;
}

/**
 * Calculate total hours including supervision using the corrected logic
 * @param {Array} items - Array of budget items
 * @returns {Number} Total hours rounded to 1 decimal place
 */
export function calculateTotalHours(items) {
  if (!Array.isArray(items)) return 0;
  
  const baseHours = calculateBaseHours(items);
  const supervisionOnlyHours = calculateSupervisionOnlyHours(items);
  const calculatedSupervisionHours = calculateAutoSupervisionHoursFromItems(items);
  
  const total = baseHours + supervisionOnlyHours + calculatedSupervisionHours;
  return Math.round(total * 10) / 10;
}

/**
 * Check if items include supervision
 * @param {Array} items - Array of budget items
 * @returns {Boolean} True if supervision item exists
 */
export function hasSupervision(items) {
  if (!Array.isArray(items)) return false;
  return items.some(item => {
    if (item.service) {
      // New structure: item has service object
      return item.service.name === "Supervisión Creativa";
    } else if (item.name) {
      // Legacy/database structure: item has direct properties
      return item.name === "Supervisión Creativa";
    }
    return false;
  });
}

/**
 * Calculate supervision hours for a single item
 * @param {Number} itemHours - Hours for a single item
 * @returns {Number} Calculated supervision hours for this item rounded to 2 decimal places
 */
export function calculateItemSupervisionHours(itemHours) {
  if (itemHours <= 0) return 0;
  
  // Step 1: Divide item hours by 6.5
  const dividedHours = itemHours / 6.5;
  
  // Step 2: Round to 2 decimal places
  const roundedDividedHours = Math.round(dividedHours * 100) / 100;
  
  // Step 3: Multiply by 1.5
  const supervisionHours = roundedDividedHours * 1.5;
  
  // Step 4: Round to 2 decimal places
  const roundedSupervisionHours = Math.round(supervisionHours * 100) / 100;
  
  return roundedSupervisionHours;
}

/**
 * Calculate supervision hours based on base hours (for new estimates)
 * Uses the new per-item calculation method
 * @param {Number} baseHours - Base hours without supervision (deprecated parameter - kept for compatibility)
 * @param {Array} items - Array of budget items (optional, for new calculation method)
 * @returns {Number} Calculated supervision hours rounded to 2 decimal places
 */
export function calculateAutoSupervisionHours(baseHours, items = null) {
  if (baseHours <= 0) return 0;
  
  // If items are provided, use the new per-item calculation method
  if (items && Array.isArray(items)) {
    return calculateAutoSupervisionHoursFromItems(items);
  }
  
  // Legacy calculation for backward compatibility
  const hours = Math.floor(baseHours / 6.5) * 1.5;
  return Math.round(hours * 100) / 100;
}

/**
 * Calculate total supervision hours from an array of items using the new per-item method
 * Only calculates supervision based on regular items (excludes supervision-only items)
 * @param {Array} items - Array of budget items
 * @returns {Number} Total supervision hours rounded to 2 decimal places
 */
export function calculateAutoSupervisionHoursFromItems(items) {
  if (!Array.isArray(items) || items.length === 0) return 0;
  
  let totalSupervisionHours = 0;
  
  // Calculate supervision for regular items only (exclude supervision-only items)
  items.forEach(item => {
    // Skip supervision items to avoid double counting
    if (item.service) {
      if (item.service.name === "Supervisión Creativa") return;
      // Skip supervision-only items - supervision should only be calculated for regular items
      if (item.supervisionOnly) return;
      
      // Calculate total hours for this regular item only
      const itemTotalHours = parseFloat(item.totalHours) || 
                            (parseFloat(item.service.hours) || parseFloat(item.service.defaultHours) || 0) * (parseFloat(item.quantity) || 1);
      
      // Add supervision hours for this regular item
      totalSupervisionHours += calculateItemSupervisionHours(itemTotalHours);
    } else if (item.name && item.hours !== undefined) {
      // Legacy/database structure
      if (item.name === "Supervisión Creativa") return;
      // Skip supervision-only items - supervision should only be calculated for regular items
      if (item.supervisionOnly) return;
      
      const itemTotalHours = parseFloat(item.totalHours) || 
                            (parseFloat(item.hours) || 0) * (parseFloat(item.quantity) || 1);
      
      totalSupervisionHours += calculateItemSupervisionHours(itemTotalHours);
    }
  });
  
  // Round final total to 2 decimal places
  return Math.round(totalSupervisionHours * 100) / 100;
}

/**
 * Parse and normalize budget items for consistent structure
 * @param {Array|String} items - Array of budget items or JSON string
 * @returns {Array} Normalized array of budget items
 */
export function parseItems(items) {
  if (!items) return [];
  
  try {
    // If items is a string, parse it as JSON
    let parsedItems = typeof items === 'string' ? JSON.parse(items) : items;
    
    if (!Array.isArray(parsedItems)) return [];
    
    // Normalize each item to ensure consistent structure
    return parsedItems.map(item => {
      if (item.service) {
        // Item already has service structure (new format)
        return {
          ...item,
          quantity: parseFloat(item.quantity) || 1,
          service: {
            ...item.service,
            hours: parseFloat(item.service.hours) || parseFloat(item.service.defaultHours) || 0,
            defaultHours: parseFloat(item.service.defaultHours) || parseFloat(item.service.hours) || 0
          },
          totalHours: item.totalHours || Math.round((parseFloat(item.service.hours || item.service.defaultHours || 0) * (parseFloat(item.quantity) || 1)) * 10) / 10
        };
      } else if (item.name && item.hours !== undefined) {
        // Legacy format - convert to new structure
        return {
          quantity: parseFloat(item.quantity) || 1,
          service: {
            name: item.name,
            hours: parseFloat(item.hours) || 0,
            defaultHours: parseFloat(item.hours) || 0,
            description: item.description || ''
          },
          totalHours: item.totalHours || Math.round((parseFloat(item.hours || 0) * (parseFloat(item.quantity) || 1)) * 10) / 10
        };
      }
      return item;
    });
  } catch (error) {
    console.error('Error parsing items:', error);
    return [];
  }
}
